import { useState } from "react";
import { Ban, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { TooltipButton } from "@/components/common/Tooltips";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

// Cancelación LÓGICA de una garantía. Visible y funcional SOLO para el administrador de la entidad.
// El backend (POST /garantias/{id}/cancelar) conserva el dato (nunca borrado físico) con motivo + sello.
export function CancelarGarantiaButton({ garantia, onDone, size = "icon" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role !== "admin_entidad") return null;
  if (["Cancelada", "Cancelado"].includes(garantia?.estatus))
    return <span className="text-[11px] font-medium text-red-500" data-testid={`garantia-cancelada-${garantia.id}`}>Cancelada</span>;

  const cancelar = async () => {
    const m = motivo.trim();
    if (m.length < 4) { toast.error("Escribe el motivo de la cancelación (mínimo 4 caracteres)"); return; }
    setBusy(true);
    try {
      await api.post(`/garantias/${garantia.id}/cancelar`, { motivo: m });
      toast.success("Garantía cancelada (dato conservado como evidencia).");
      setOpen(false);
      setMotivo("");
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cancelar la garantía");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <TooltipButton
        tooltip="Cancelar esta garantía (solo administrador; se conserva como evidencia)"
        data-testid={`cancelar-garantia-btn-${garantia.id}`}
        variant="ghost"
        size={size === "icon" ? "icon" : "sm"}
        className="h-8 gap-1.5 px-2.5 text-red-600 hover:bg-red-50 hover:text-red-700"
        onClick={() => setOpen(true)}
      >
        <Ban className="h-4 w-4" /> {size !== "icon" && "Cancelar"}
      </TooltipButton>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="cancelar-garantia-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Cancelar garantía {garantia?.tipo_garantia ? `(${garantia.tipo_garantia})` : ""} {garantia?.numero_credito || ""}
            </AlertDialogTitle>
            <AlertDialogDescription>
              La cancelación es lógica: la garantía se conserva como evidencia y queda en bitácora con
              quién, cuándo y por qué. Esta acción no borra el registro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div>
            <Label className="mb-1 block text-xs">Motivo de la cancelación <span className="text-red-500">*</span></Label>
            <Textarea
              data-testid="cancelar-garantia-motivo"
              rows={2}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Ej. sustitución de colateral, error de captura, liberación..."
            />
            <p className="mt-1 text-[11px] text-slate-400">Obligatorio. Quedará en la bitácora de la entidad.</p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Volver</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirmar-cancelar-garantia"
              onClick={(e) => { e.preventDefault(); cancelar(); }}
              disabled={busy || motivo.trim().length < 4}
              className="bg-red-600 hover:bg-red-700"
            >
              {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Confirmar cancelación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
